import BulkActionNewEntryModal from "@/common/components/BulkActionNewEntryModal"
import Button from "@/common/components/Button"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import LoadingSpinner from "@/common/components/LoadingSpinner"
import FilterCheckbox from "@/common/components/FilterCheckbox"
import Select from "@/common/components/Select"
import { SheetFilters } from "@/common/components/SheetFilters"
import TextField from "@/common/components/TextField"
import { equipmentStatusTranslation } from "@/common/constants/equipmentStatus.constant"
import useCompany from "@/common/hooks/queries/useCompany"
import { useCompanyEquipments } from "@/common/hooks/queries/useCompanyEquipments"
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories"
import useDebounce from "@/common/hooks/useDebounce"
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper"
import { CompanyBranchData } from "@/common/types/companyBranches.type"
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories"
import { EquipmentsGridTable } from "@/features/privateCompanies/components/EquipmentsGridTable"
import { options as propertyKindOptions } from "@/features/privateCompanies/constants/propertyKindOptions.constant"
import { options as usageKindOptions } from "@/features/privateCompanies/constants/usageKindOptions.constant"
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches"
import { CompanyEquipmentData } from "@/features/privateCompanies/types/companyEquipments.type"
import { useQueryClient } from "@tanstack/react-query"
import { SearchIcon } from "lucide-react"
import { ChangeEvent } from "react"
import { Link, useParams } from "react-router-dom"

const EquipmentList = () => {
  const { id: companyId } = useParams()

  const { data: company } = useCompany({ id: companyId || '' });

  const queryClient = useQueryClient();

  const { filterParams, setFilterParams } = useFilterParams({
    status: { label: 'Ativo', value: 'enabled' }
  });

  const hasFilterParmas = Object.keys(filterParams).some((key) => key !== 'page')

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [daysWithoutUsage, setDaysWithoutUsage] = useDebounce(filterParams.daysWithoutUsage || '');

  const [searchCategory, setSearchCategory, instantSearchCategory] = useDebounce(filterParams.category?.label || '');
  const [searchBranch, setSearchBranch, instantSearchBranch] = useDebounce(filterParams.branch?.label || '');

  const {
    data: companyEquipments,
    meta,
    isPending: isPendingCompanyEquipments,
    isFetching: isFetchingCompanyEquipments,
    error: companyEquipmentsError = {} as any,
    refetch: refetchCompanyEquipments,
  } = useCompanyEquipments<CompanyEquipmentData[]>({
    filters: {
      companyId: companyId || '',
      search: search,
      page: filterParams.page || 1,
      categoryId: filterParams.category?.value,
      status: filterParams.status?.value,
      usageKind: filterParams.usageKind?.value,
      propertyKind: filterParams.propertyKind?.value,
      branchId: filterParams.branch?.value,
      daysWithoutUsage: daysWithoutUsage,
    },
    sort: {
      lastConsumptionDate: filterParams.lastConsumptionDate
    }
  })

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: searchCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const handleChangeDaysWithoutUsage = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseInt(e.target.value) : ''
    if (typeof value === 'number' && (value < 1 || value > 730)) return
    setDaysWithoutUsage(`${value}`)
    setFilterParams((prev) => ({ ...prev, daysWithoutUsage: `${value}` }))
  }

  const disableCreateButton = isPendingCompanyEquipments || companyEquipmentsError?.status === 403;

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyEquipmentsError?.status}
      refetchFunction={refetchCompanyEquipments}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Select
                placeholder="Tipo de Equipamento"
                className="font-light text-xs rounded-xl shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]"
                badge
                value={filterParams.category?.value ? filterParams.category : null}
                onChange={(e) => setFilterParams((prev) => ({ ...prev, category: e }))}
                onInputChange={(inputValue) => setSearchCategory(inputValue)}
                options={[{ label: 'Todos', value: '' }, ...(equipmentCategoryOptions || [])]}
              />
              <Select
                placeholder="Status"
                className="font-light text-xs rounded-xl shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]"
                badge
                value={filterParams.status?.value ? filterParams.status : null}
                onChange={(e) => setFilterParams((prev) => ({ ...prev, status: e }))}
                options={[{ label: 'Todos', value: '' }, ...Object.entries(equipmentStatusTranslation).map(([key, value]) => ({ label: value, value: key }))]}
              />
              <TextField
                placeholder="Dias sem compra"
                className="w-36 font-light text-xs rounded-xl shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]"
                variant="badge"
                type="number"
                min={1}
                max={730}
                value={filterParams.daysWithoutUsage || ''}
                onChange={handleChangeDaysWithoutUsage}
              />
              <div className="flex items-center gap-2">
                <SheetFilters
                  filters={[
                    {
                      label: 'Tipo de Equipamento',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.category?.value,
                          onClick: () => setFilterParams(({ category, ...prev }) => ({ ...prev }))
                        },
                        ...(equipmentCategoryOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.category?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, category: { label, value } }))
                        })) || [])
                      ],
                      component: (
                        <TextField
                          variant="filter"
                          LeftIcon={<SearchIcon size={14} />}
                          placeholder="Pesquisar"
                          className="mb-2"
                          value={instantSearchCategory}
                          onChange={(e) => setSearchCategory(e.currentTarget.value)}
                        />
                      ),
                    },
                    {
                      label: 'Status',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.status?.value,
                          onClick: () => setFilterParams(({ status, ...prev }) => ({ ...prev }))
                        },
                        ...Object.entries(equipmentStatusTranslation).map(([key, value]) => ({
                          label: value,
                          value: key,
                          selected: filterParams.status?.value === key,
                          onClick: () => setFilterParams((prev) => ({ ...prev, status: { label: value, value: key } }))
                        }))
                      ]
                    },
                    {
                      label: 'Dias sem compra',
                      component: (
                        <TextField
                          placeholder="Dias sem compra"
                          type="number"
                          min={1}
                          max={730}
                          value={filterParams.daysWithoutUsage}
                          onChange={handleChangeDaysWithoutUsage}
                        />
                      )
                    },
                    {
                      label: 'Tipo de uso',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.usageKind?.value,
                          onClick: () => setFilterParams(({ usageKind, ...prev }) => ({ ...prev }))
                        },
                        ...usageKindOptions.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.usageKind?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, usageKind: { label, value } }))
                        }))
                      ]
                    },
                    {
                      label: 'Tipo de propriedade',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.propertyKind?.value,
                          onClick: () => setFilterParams(({ propertyKind, ...prev }) => ({ ...prev }))
                        },
                        ...propertyKindOptions.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.propertyKind?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, propertyKind: { label, value } }))
                        }))
                      ]
                    },
                    {
                      label: 'Filial',
                      options: [
                        {
                          label: 'Todos',
                          value: '',
                          selected: !filterParams.branch?.value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, branch: null }))
                        },
                        ...(companyBranchOptions?.map(({ label, value }) => ({
                          label,
                          value,
                          selected: filterParams.branch?.value === value,
                          onClick: () => setFilterParams((prev) => ({ ...prev, branch: { label, value } }))
                        })) || []),
                      ],
                      component: (
                        <TextField
                          variant="filter"
                          LeftIcon={<SearchIcon size={14} />}
                          placeholder="Pesquisar"
                          className="mb-2"
                          value={instantSearchBranch}
                          onChange={(e) => setSearchBranch(e.currentTarget.value)}
                        />
                      ),
                    },
                  ]}
                />

                <FilterCheckbox
                  isChecked={filterParams.lastConsumptionDate === 'true'}
                  onChange={(value: any) => {
                    setFilterParams((prev) => ({
                      ...prev, lastConsumptionDate: value ? true : null,
                    }))
                  }}
                  label="Ordenar por último consumo"
                  id="filter-checkbox"
                  name="filter-checkbox"
                />
              </div>

              {hasFilterParmas && (
                <Button
                  variant='link'
                  className="px-4"
                  onClick={() => setFilterParams(() => ({}))}
                >
                  Limpar Filtros
                </Button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <LoadingSpinner isLoading={isFetchingCompanyEquipments} />
              <BulkActionNewEntryModal
                operationKlassToCheckPermission="Admin::BulkActions::Entries::RequestTemplate"
                options={[
                  {
                    label: 'Cadastrar equipamentos (Infocar)',
                    actionerKlass: 'BulkActions::Admin::InfocarEnabledClientEquipmentsImporter',
                  },
                  {
                    label: 'Cadastrar equipamentos (Não Infocar)',
                    actionerKlass: 'BulkActions::Admin::ClientEquipmentsImporter',
                  },
                ]}
                onEntryCompleted={() => {
                  queryClient.invalidateQueries({ queryKey: ['companyEquipments'] });
                }}
                globalColumnsDefaultValues={{
                  client_company_id: {
                    value: companyId || '',
                    label: company?.general_information?.fantasy_name || '',
                  }
                }}
              />

              <Link
                to={disableCreateButton ? '' : `/companies/${companyId}/equipments/create`}
                className={disableCreateButton ? 'cursor-default' : ''}
              >
                <Button
                  name="create_equipment"
                  className="flex items-center rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                  disabled={disableCreateButton}
                  operationKlassToCheckPermission="Admin::Client::Equipments::Create"
                >
                  Novo Equipamento
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <EquipmentsGridTable
          className="max-h-[calc(100vh-356px)]"
          companyId={companyId}
          companyEquipments={companyEquipments || []}
          page={filterParams.page || 1}
          totalPages={meta.total_pages || 1}
          totalCount={meta.total}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          isLoadingData={isPendingCompanyEquipments}
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default EquipmentList
